import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export interface TestimonialsProps {
  headline: string;
  testimonials: { quote: string; author: string; context?: string; rating: number }[];
  cta?: { text: string; href: string };
}

export default function Testimonials({ headline, testimonials, cta }: TestimonialsProps) {
  const [active, setActive] = useState(0);
  const t = testimonials[active];
  const { ref, visible } = useScrollAnimation();

  return (
    <section className="bg-jc-charcoal py-24 md:py-36 overflow-hidden relative">
      {/* Gold accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-jc-gold/30 to-transparent hidden lg:block"></div>

      <div
        ref={ref}
        className="max-w-6xl mx-auto px-6 md:px-10"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(28px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
      >
        {/* Section label */}
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-6 h-px bg-jc-gold/40"></span>
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-jc-gold/80">Client Stories</span>
            <span className="w-6 h-px bg-jc-gold/40"></span>
          </div>
          <h2 className="font-serif text-[clamp(2rem,4vw,3.2rem)] font-light text-white leading-[1.12]">{headline}</h2>
        </div>

        {/* Quote card */}
        <div className="relative bg-white/4 border border-white/8 rounded-3xl px-8 md:px-16 py-14 overflow-hidden">
          {/* Giant quote mark */}
          <span
            className="font-serif text-[120px] text-jc-gold/10 leading-none absolute -top-4 left-8 select-none"
            aria-hidden="true"
          >
            &ldquo;
          </span>

          {/* Stars */}
          <div className="flex gap-1.5 mb-7 relative z-10">
            {[...Array(t.rating)].map((_, i) => (
              <i key={i} className="ri-star-fill text-jc-gold text-base"></i>
            ))}
          </div>

          {/* Quote text */}
          <blockquote className="font-serif text-[1.35rem] md:text-[1.6rem] font-light text-white/85 italic leading-relaxed mb-10 relative z-10 max-w-3xl">
            &ldquo;{t.quote}&rdquo;
          </blockquote>

          {/* Author */}
          <div className="flex items-center gap-5 relative z-10">
            <div className="w-12 h-12 flex items-center justify-center bg-jc-gold/15 border border-jc-gold/25 rounded-full flex-shrink-0">
              <i className="ri-user-3-line text-jc-gold text-lg"></i>
            </div>
            <div>
              <p className="font-semibold text-white text-sm">{t.author}</p>
              {t.context && <p className="text-white/35 text-xs mt-0.5">{t.context}</p>}
            </div>
          </div>

          {/* Nav dots */}
          {testimonials.length > 1 && (
            <div className="absolute bottom-8 right-8 flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${i === active ? 'bg-jc-gold w-6' : 'bg-white/20 w-2 hover:bg-white/40'}`}
                  aria-label={`Testimonial ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {cta && (
          <div className="mt-10 text-center">
            <a href={cta.href}
              className="inline-flex items-center gap-2.5 px-8 py-4 border border-white/20 text-white text-[10px] font-bold tracking-[0.18em] uppercase rounded-full hover:bg-jc-gold hover:text-jc-charcoal hover:border-jc-gold transition-all duration-300 whitespace-nowrap group">
              {cta.text} <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
